import React, { Fragment } from 'react';
import { Dropdown, Tooltip } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import Skeleton from 'react-loading-skeleton';
import { useQueryClient } from 'react-query';
import CustomFilterView from 'routes/screens/components/CustomFilterView';
import Spacer from 'shared/components/Spacer';
import { useGetTriggersCount } from 'shared/hooks';
import { DrawerMenuContainer, DrawerMenuList } from 'shared/styles';
import { FilterViewType, SurveyType } from 'shared/types';
import {
  useGetAllSurveysInfinite,
  useGetSurveyResponseCount,
} from '../../../hooks';
import { getSurveyStateNameFromState } from '../utils';
import { SURVEY_THEMES } from '../../../constants';
import { DrawerBody, StyledMenuItem, SurveyState } from '../style';

interface IAllSurveys {
  setCurrentSurvey: (surveyId: string) => void;
  toggleDrawer: () => void;
  currentSurvey: any;
  onSelect: (surveyId: string) => void;
  parent: 'RESPONSE' | 'QUEUE';
  setAppliedFilters?: (value: any) => void;
  allFilterViews?: any[];
  onFilterViewChange?: (value: FilterViewType) => void;
  onFilterViewDelete?: (filterId: string, filterName: string) => void;
  selectedFilter?: FilterViewType;
  setselectedFilter?: (filter: FilterViewType | null) => void;
  setselectedFilterViewId?: (filterViewId: string | null) => void;
  setSelectedFilterView?: (filterViewId: string | null) => void;
  setFilterViewEditAnchorEl?: (value: any) => void;
  setFilterViewName?: (filterViewName: string) => void;
  editFilterViewRef?: any;
}

const ResponseDrawer = (props: IAllSurveys) => {
  const { t } = useTranslation();
  const {
    data,
    isFetching,
    refetch,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetAllSurveysInfinite();

  const { ref: loadMoreBtnRef, inView } = useInView();

  const {
    setCurrentSurvey,
    toggleDrawer,
    currentSurvey,
    onSelect,
    setAppliedFilters,
    parent,
    allFilterViews,
    onFilterViewChange,
    onFilterViewDelete,
    selectedFilter,
    setselectedFilter,
    setselectedFilterViewId,
    setFilterViewName,
    setFilterViewEditAnchorEl,
    editFilterViewRef,
  } = props;

  const filteredFilterViews: Record<string, FilterViewType[]> = {};

  allFilterViews?.forEach((filterView) => {
    data?.pages[0]?.surveys?.forEach((survey) => {
      if (filterView.conditions[0].name === survey.id) {
        if (!filteredFilterViews[survey.name]) {
          filteredFilterViews[survey.name] = [];
        }
        filteredFilterViews[survey.name].push(filterView);
      }
    });
  });

  const queryClient = useQueryClient();
  const [selectedTheme, setSelectedTheme] = React.useState({
    ...SURVEY_THEMES[0],
    value: `${t(SURVEY_THEMES[0].value)}`,
  });

  const selectSurvey = (survey: any) => {
    toggleDrawer();
    setCurrentSurvey(survey.id);
    onSelect(survey.id);
    // to reset filters after changing the survey
    setselectedFilter?.(null);
    setselectedFilterViewId?.(null);
    setAppliedFilters?.({ localState: {}, isApply: true, filterParams: '' });
  };

  const handleThemeChange = (item) => {
    setSelectedTheme(item);
  };

  React.useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  React.useEffect(() => {
    refetch();
  }, [data]);

  React.useEffect(() => {
    queryClient.invalidateQueries('surveys');
  }, []);

  return (
    <DrawerBody className="all-list">
      <div className="category-menu__section">
        <div className="category-menu__section-header">
          <Dropdown
            id="modal-dd-survey-theme"
            label={`${t('responses.select_survey_theme')}`}
            defaultSelectedItem={selectedTheme}
            emptyItemsText={`${t('no_results_found')}`}
            items={SURVEY_THEMES.map((e) => ({ ...e, value: `${t(e.value)}` }))}
            onChange={(item) => handleThemeChange(item)}
            itemToString={(item) => item.value}
          />
          <Spacer y={1} />
          {onFilterViewDelete != undefined &&
            onFilterViewChange != undefined && (
              <DrawerMenuContainer $isResponse={true}>
                <DrawerMenuList $isResponse={true}>
                  <CustomFilterView
                    onChange={onFilterViewChange}
                    onDelete={onFilterViewDelete}
                    toggleDrawer={toggleDrawer}
                    selectedFilter={(selectedFilter as FilterViewType) ?? []}
                    isFetchingFilterViews={isFetching}
                    filteredViews={filteredFilterViews ?? []}
                    editFilterViewRef={editFilterViewRef}
                    setEditFilterViewAnchor={setFilterViewEditAnchorEl}
                    setFilterViewName={setFilterViewName}
                    groupBySurvey
                  />
                </DrawerMenuList>
              </DrawerMenuContainer>
            )}
          {data?.pages?.length &&
          data.pages[0].surveys?.filter((survey: SurveyType) =>
            selectedTheme.id !== 'ALL'
              ? survey?.type === selectedTheme.id
              : survey,
          ).length ? (
            <label> {t('sidebar.surveys')} </label>
          ) : null}
        </div>

        {data?.pages?.map((page: any, index: number) => (
          <React.Fragment key={index}>
            {page?.surveys
              ?.filter((survey: SurveyType) =>
                selectedTheme.id !== 'ALL'
                  ? survey?.type === selectedTheme.id
                  : survey,
              )
              .map((item: SurveyType) => (
                <Fragment key={item.id}>
                  <SurveyName
                    isCurrentSurvey={currentSurvey === item.id}
                    surveyInfo={item}
                    selectSurvey={selectSurvey}
                    parent={parent}
                    selectedFilter={selectedFilter}
                  />
                </Fragment>
              ))}
          </React.Fragment>
        ))}
        {isFetching && !isFetchingNextPage ? (
          <>
            {[1, 2, 3].map((a) => (
              <p key={a} style={{ margin: '10px' }}>
                <Skeleton width="80%" height={24} />
              </p>
            ))}
          </>
        ) : null}

        {data?.pages?.length &&
        selectedTheme.id !== 'ALL' &&
        !data.pages[0].surveys?.filter(
          (survey: SurveyType) => survey?.type === selectedTheme.id,
        ).length ? (
          <h5 className="empty">{t('no_survey_found')}</h5>
        ) : null}
        {isFetchingNextPage ? (
          <>
            {[1, 2, 3].map((a) => (
              <p key={a} style={{ margin: '10px' }}>
                <Skeleton width="80%" height={24} />
              </p>
            ))}
          </>
        ) : hasNextPage ? (
          <button
            className="loadnew"
            ref={loadMoreBtnRef}
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage || isFetchingNextPage}
          >
            {t('load_more')}
          </button>
        ) : null}
      </div>
    </DrawerBody>
  );
};

const SurveyName = (props: SurveyNameType) => {
  const { t } = useTranslation();
  const { isCurrentSurvey, selectSurvey, surveyInfo, parent, selectedFilter } =
    props;

  const { data: responseCount, isFetching } = useGetSurveyResponseCount({
    surveyId: surveyInfo.id || '',
    refetchOnWindowFocus: true,
    enabled: parent === 'RESPONSE' && surveyInfo.state !== 'DRAFT',
  });

  const { data: triggersCount, isFetching: isTriggerFetching } =
    useGetTriggersCount({
      surveyId: surveyInfo.id || '',
      enabled: parent === 'QUEUE',
      filters: { filterParams: {}, localState: {}, isApply: false },
    });
  const { name, state } = surveyInfo;
  const totalCount =
    parent === 'RESPONSE' ? responseCount?.count : triggersCount?.count;
  return (
    <StyledMenuItem
      className={isCurrentSurvey && !selectedFilter ? 'selected' : ''}
      onClick={() => (state !== 'DRAFT' ? selectSurvey(surveyInfo) : null)}
    >
      {state === 'DRAFT' && Number(totalCount) === 0 ? (
        <Tooltip label={`${t('tooltip.activate_survey')}`} position="left">
          <div className={`surveyName ${state}`}>
            {name}&nbsp;
            {isFetching || isTriggerFetching ? (
              <Skeleton width={30} height={14} />
            ) : (
              <span style={{ display: 'inline-block' }}>
                {Number(totalCount) ? `(${totalCount})` : ''}
              </span>
            )}
          </div>
        </Tooltip>
      ) : (
        <div className={`surveyName ${state}`}>
          {name}&nbsp;
          {isFetching || isTriggerFetching ? (
            <Skeleton width={30} height={14} />
          ) : (
            <span style={{ display: 'inline-block' }}>
              {Number(totalCount) ? `(${totalCount})` : ''}
            </span>
          )}
        </div>
      )}
      {state === 'DRAFT' || state === 'PAUSE' ? (
        <SurveyState statename={state}>
          {t(getSurveyStateNameFromState(state))}
        </SurveyState>
      ) : null}
    </StyledMenuItem>
  );
};

export default ResponseDrawer;

type SurveyNameType = {
  isCurrentSurvey: boolean;
  selectSurvey: (survey: SurveyType) => void;
  surveyInfo: SurveyType;
  parent: 'RESPONSE' | 'QUEUE';
  selectedFilter?: FilterViewType;
};
